import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, timer } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from './store/login/login.state';
import { switchMap, take, catchError, filter, finalize, timeout, timeoutWith } from 'rxjs/operators';
import { DoForceLogoutAction } from './store/login/login.action';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(private store: Store<AppState>) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.store.select(appStore => appStore.login.jwt).pipe(
            take(1),
            switchMap(jwt => {
                if (jwt != null) {
                    const authReq = req.clone({headers: req.headers.set('Authorization', 'Bearer ' + jwt)});
                    return next.handle(authReq).pipe(
                        catchError(error => {
                            if (error instanceof HttpErrorResponse && error.status === 401) {
                                return this.handle401Error(req, next, jwt);
                            }
                            return throwError(error);
                        })
                    );
                } else {
                    return next.handle(req);
                }
            })
        );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler, currentJwt: string): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            
            // Wait for a new JWT that's different from the one that failed
            return this.store.select(appStore => appStore.login.jwt).pipe(
                filter(jwt => jwt != null && jwt !== currentJwt),
                take(1),
                timeout(3000), // 3 second timeout for JWT refresh
                switchMap(newJwt => {
                    this.refreshTokenSubject.next(newJwt);
                    const authReq = request.clone({headers: request.headers.set('Authorization', 'Bearer ' + newJwt)});
                    return next.handle(authReq);
                }),
                finalize(() => {
                    this.isRefreshing = false;
                }),
                catchError(retryError => {
                    if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
                        this.store.dispatch(new DoForceLogoutAction());
                    } else if (retryError.name === 'TimeoutError') {
                        this.store.dispatch(new DoForceLogoutAction());
                    }
                    return throwError(retryError);
                })
            );
        } else {
            // Request is already refreshing, wait for completion
            return this.refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(jwt => {
                    const authReq = request.clone({headers: request.headers.set('Authorization', 'Bearer ' + jwt)});
                    return next.handle(authReq);
                })
            );
        }
    }
}
